import { motion } from 'framer-motion';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { PageTransition } from '../../components/Transitions/PageTransition';
import { Spinner } from '../../components/Feedback/Spinner';

export function SignupPage() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const { t } = useTranslation();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError(t('auth.passwordsMismatch'));
      return;
    }
    if (password.length < 6) {
      setError(t('auth.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      await signup(firstName, lastName, phone, password, email.trim());
      toast.success(t('auth.signedIn'));
      navigate('/onboarding');
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.signupError');
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <svg className="w-14 h-14 mx-auto" viewBox="0 0 100 100" fill="none" aria-hidden="true">
              <defs>
                <linearGradient id="ra3" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#0d7377"/>
                  <stop offset="100%" stopColor="#095f62"/>
                </linearGradient>
              </defs>
              <rect width="100" height="100" rx="22" fill="url(#ra3)"/>
              <path d="M28 72 Q28 44 50 32 Q72 44 72 72" fill="none" stroke="white" strokeWidth="5.5" strokeLinecap="round"/>
              <path d="M36 72 Q36 52 50 44 Q64 52 64 72" fill="none" stroke="white" strokeWidth="5.5" strokeLinecap="round" opacity="0.35"/>
              <line x1="50" y1="20" x2="50" y2="32" stroke="white" strokeWidth="5.5" strokeLinecap="round"/>
              <circle cx="50" cy="18" r="4" fill="white"/>
            </svg>
            <h1 className="mt-4 text-3xl font-bold text-text-primary">{t('auth.signupTitle')}</h1>
            <p className="mt-2 text-text-muted">{t('auth.signupSubtitle')}</p>
          </div>

          <div className="bg-surface rounded-xl shadow-sm border border-border p-8">
            <form onSubmit={handleSignup}>
              <div className="mb-4 grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-text-secondary mb-2">
                    {t('auth.firstName')}
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t('auth.firstNamePlaceholder')}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent bg-background"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-text-secondary mb-2">
                    {t('auth.lastName')}
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t('auth.lastNamePlaceholder')}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent bg-background"
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="phone" className="block text-sm font-medium text-text-secondary mb-2">
                  {t('auth.phoneLabel')}
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('auth.phonePlaceholder')}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent bg-background"
                  required
                />
              </div>

              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-text-secondary mb-2">
                  {t('auth.reset.signupEmailLabel')}
                </label>
                <input
                  id="email"
                  type="email"
                  dir="ltr"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  aria-describedby="email-hint"
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent bg-background"
                />
                <p id="email-hint" className="mt-1.5 text-xs text-text-muted">{t('auth.reset.signupEmailHint')}</p>
              </div>

              <div className="mb-4">
                <label htmlFor="password" className="block text-sm font-medium text-text-secondary mb-2">
                  {t('auth.password')}
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.passwordPlaceholder')}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent bg-background"
                  required
                />
              </div>

              <div className="mb-6">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-secondary mb-2">
                  {t('auth.confirmPassword')}
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('auth.confirmPasswordPlaceholder')}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent bg-background"
                  required
                />
              </div>

              {error && (
                <div className="mb-4 p-3 bg-status-danger/10 text-status-danger text-sm rounded-lg">
                  {error}
                </div>
              )}

              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className="w-full py-3 bg-brand-primary text-white font-medium rounded-lg hover:bg-brand-secondary disabled:opacity-50 transition-colors"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Spinner className="h-4 w-4 border-white border-t-transparent" />
                    {t('auth.signingUp')}
                  </span>
                ) : (
                  t('auth.createAccount')
                )}
              </motion.button>

              <p className="mt-6 text-center text-sm text-text-muted">
                {t('auth.haveAccount')}{' '}
                <Link to="/login" className="text-brand-primary font-medium hover:underline">
                  {t('auth.signIn')}
                </Link>
              </p>
            </form>
          </div>

          <p className="mt-6 text-center text-sm text-text-muted">
              <Trans
                i18nKey="auth.terms"
                components={{
                  terms: <Link to="/terms" className="underline underline-offset-2 hover:text-text-primary" />,
                  privacy: <Link to="/privacy" className="underline underline-offset-2 hover:text-text-primary" />,
                }}
              />
            </p>
        </div>
      </div>
    </PageTransition>
  );
}
